"use client"

import { useCallback, useReducer } from "react"

import { submitForgeForm } from "@/features/forges"

import type { FormAction, FormState, Question } from "../types"

const initialState: FormState = {
  currentQuestionIndex: 0,
  answers: [],
  currentAnswer: "",
  hasError: false,
  isFading: false,
  isCompleted: false,
}

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case "SET_ANSWER":
      return { ...state, currentAnswer: action.payload }
    case "SET_ERROR":
      return { ...state, hasError: action.payload }
    case "SET_FADING":
      return { ...state, isFading: action.payload }
    case "NEXT_QUESTION":
      return {
        ...state,
        answers: [...state.answers, state.currentAnswer],
        currentAnswer: "",
        currentQuestionIndex: state.currentQuestionIndex + 1,
        hasError: false,
      }
    case "COMPLETE_FORM":
      return {
        ...state,
        answers: [...state.answers, action.payload],
        currentAnswer: "",
        isCompleted: true,
        hasError: false,
      }
    case "RESET_ERROR":
      return { ...state, hasError: false }
    default:
      return state
  }
}

export function useForgeQuestionForm(questions: Question[]) {
  const [state, dispatch] = useReducer(formReducer, initialState)

  const totalQuestions = questions.length
  const isLastQuestion = state.currentQuestionIndex === totalQuestions - 1
  const currentQuestion = questions[state.currentQuestionIndex] || null
  const progressValue =
    (state.currentQuestionIndex / (totalQuestions - 1)) * 100
  const questionsLeft = totalQuestions - state.currentQuestionIndex - 1

  const handleNextQuestion = useCallback(async () => {
    if (state.currentAnswer.trim() === "") {
      dispatch({ type: "SET_ERROR", payload: true })
      return
    }

    dispatch({ type: "SET_FADING", payload: true })

    setTimeout(async () => {
      if (!isLastQuestion) {
        dispatch({ type: "NEXT_QUESTION" })
      } else {
        const allAnswers = [...state.answers, state.currentAnswer]
        const result = await submitForgeForm(allAnswers)

        if (result.success) {
          dispatch({ type: "COMPLETE_FORM", payload: state.currentAnswer })
        } else {
          console.error("Failed to submit form:", result.error)
          dispatch({ type: "SET_ERROR", payload: true })
        }
      }
      setTimeout(() => dispatch({ type: "SET_FADING", payload: false }), 150)
    }, 300)
  }, [state.currentAnswer, state.answers, isLastQuestion, questions])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault()
        handleNextQuestion()
      }
    },
    [handleNextQuestion]
  )

  const handleAnswerChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (state.hasError) {
        dispatch({ type: "RESET_ERROR" })
      }
      dispatch({ type: "SET_ANSWER", payload: e.target.value })
    },
    [state.hasError]
  )

  const getCardStyle = useCallback(() => {
    return state.currentQuestionIndex === 0
      ? "bg-white shadow-[0px_0px_40px_0px_rgba(100,103,198,0.30)]"
      : "bg-gray-50"
  }, [state.currentQuestionIndex])

  return {
    form: {
      currentAnswer: state.currentAnswer,
      hasError: state.hasError,
      isFading: state.isFading,
      isCompleted: state.isCompleted,
    },
    question: {
      isLastQuestion,
      currentQuestion,
      progressValue,
      questionsLeft,
    },
    handlers: {
      handleNextQuestion,
      handleKeyDown,
      handleAnswerChange,
    },
    getCardStyle,
  }
}
